import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

/**
 * One combined pass for everything that is cheap per pixel: radial speed blur,
 * chromatic aberration, a teal/magenta grade, scanlines, grain and vignette.
 * Splitting these into separate passes would cost several extra fullscreen
 * blits for no visual gain.
 */
const CyberShader = {
  name: 'CyberShader',
  uniforms: {
    tDiffuse:    { value: null },
    uTime:       { value: 0 },
    uSpeed:      { value: 0 },    // 0..1 normalised player speed
    uAberration: { value: 1 },
    uGrain:      { value: 1 },
    uVignette:   { value: 1 },
    uHurt:       { value: 0 },
    uFlash:      { value: 0 },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uWarp:       { value: 0 },    // portal transition ripple
  },
  vertexShader: /* glsl */`
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */`
    precision highp float;
    uniform sampler2D tDiffuse;
    uniform float uTime, uSpeed, uAberration, uGrain, uVignette, uHurt, uFlash, uWarp;
    uniform vec2 uResolution;
    varying vec2 vUv;

    float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

    void main() {
      vec2 uv = vUv;
      vec2 c = uv - 0.5;
      float r = length(c);

      // Portal transit ripple: a short outward pulse right after a teleport.
      if (uWarp > 0.001) {
        float ring = sin(r * 26.0 - uWarp * 12.0) * exp(-r * 3.0);
        uv += normalize(c + 1e-6) * ring * 0.012 * uWarp;
      }

      // Radial smear that ramps in with speed. Six taps is enough because the
      // offsets are tiny; more just costs bandwidth.
      vec3 col = vec3(0.0);
      float blur = uSpeed * uSpeed * 0.055;
      if (blur > 0.0008) {
        float total = 0.0;
        for (int i = 0; i < 6; i++) {
          float t = float(i) / 5.0;
          float w = 1.0 - t * 0.72;
          vec2 o = uv - c * t * blur * smoothstep(0.12, 0.75, r);
          col += texture2D(tDiffuse, o).rgb * w;
          total += w;
        }
        col /= total;
      } else {
        col = texture2D(tDiffuse, uv).rgb;
      }

      // Lens chromatic aberration, stronger at the edges and with speed.
      float ab = (0.0012 + uSpeed * 0.0042) * uAberration * smoothstep(0.05, 0.9, r);
      if (ab > 0.00005) {
        col.r = texture2D(tDiffuse, uv + c * ab).r;
        col.b = texture2D(tDiffuse, uv - c * ab).b;
      }

      // Grade: cool crushed shadows, warm bright ends.
      vec3 shadowTint = vec3(0.72, 0.94, 1.06);
      vec3 highTint   = vec3(1.06, 0.93, 1.02);
      float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
      col *= mix(shadowTint, highTint, smoothstep(0.05, 0.8, lum));
      col = (col - 0.5) * 1.06 + 0.5;

      // CRT scanlines, locked to physical pixels so they never shimmer.
      float scan = sin(vUv.y * uResolution.y * 1.35) * 0.5 + 0.5;
      col *= 1.0 - scan * 0.045;

      // Grain
      float g = hash(vUv * uResolution.xy + fract(uTime) * 331.0) - 0.5;
      col += g * 0.018 * uGrain * (0.35 + 0.65 * smoothstep(0.0, 0.35, lum));

      // Vignette
      col *= mix(1.0, smoothstep(0.95, 0.28, r), uVignette);

      // Damage and impact overlays
      col = mix(col, vec3(0.85, 0.06, 0.16), uHurt * smoothstep(0.15, 0.75, r) * 0.75);
      col += uFlash;

      gl_FragColor = vec4(col, 1.0);
    }
  `,
};

export class PostFX {
  constructor(engine, scene, camera) {
    this.engine = engine;
    const renderer = engine.renderer;
    const tier = engine.tier;

    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    const target = new THREE.WebGLRenderTarget(size.x, size.y, {
      type: THREE.HalfFloatType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      colorSpace: THREE.LinearSRGBColorSpace,
      depthBuffer: true,
      stencilBuffer: false,
      samples: 0,
    });

    this.composer = new EffectComposer(renderer, target);
    this.composer.setPixelRatio(engine.pixelRatio);
    this.composer.setSize(engine.width, engine.height);

    this.renderPass = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass);

    // Threshold high enough that only genuine emissives bloom; lit surfaces
    // stay crisp instead of dissolving into haze.
    this.bloom = new UnrealBloomPass(
      new THREE.Vector2(size.x * tier.bloomScale, size.y * tier.bloomScale),
      tier.bloomStrength, 0.6, 0.78,
    );
    this.bloom.enabled = tier.bloom;
    this.composer.addPass(this.bloom);

    this.cyber = new ShaderPass(CyberShader);
    this.cyber.uniforms.uResolution.value.set(size.x, size.y);
    this.cyber.uniforms.uGrain.value = tier.grain ? 1 : 0;
    this.cyber.uniforms.uAberration.value = tier.chromatic ? 1 : 0;
    this.composer.addPass(this.cyber);

    this.output = new OutputPass();
    this.composer.addPass(this.output);

    this.speed = 0;
    this._warp = 0;
    this._flash = 0;
    this._hurt = 0;
  }

  setCamera(camera) { this.renderPass.camera = camera; }
  setScene(scene) { this.renderPass.scene = scene; }

  onTier(tier) {
    this.bloom.enabled = tier.bloom;
    this.bloom.strength = tier.bloomStrength;
    this.cyber.uniforms.uGrain.value = tier.grain ? 1 : 0;
    this.cyber.uniforms.uAberration.value = tier.chromatic ? 1 : 0;
    this.resize();
  }

  resize() {
    const engine = this.engine;
    this.composer.setPixelRatio(engine.pixelRatio);
    this.composer.setSize(engine.width, engine.height);
    const size = engine.renderer.getDrawingBufferSize(new THREE.Vector2());
    this.cyber.uniforms.uResolution.value.set(size.x, size.y);
    this.bloom.setSize(size.x * engine.tier.bloomScale, size.y * engine.tier.bloomScale);
  }

  /** Kick the transition ripple. */
  warp(amount = 1) { this._warp = amount; }
  flash(amount = 0.5) { this._flash = amount; }
  hurt(amount = 1) { this._hurt = Math.max(this._hurt, amount); }

  update(dt, elapsed, normalisedSpeed) {
    this.speed += (normalisedSpeed - this.speed) * Math.min(1, dt * 8);
    this._warp = Math.max(0, this._warp - dt * 3.4);
    this._flash = Math.max(0, this._flash - dt * 3.0);
    this._hurt = Math.max(0, this._hurt - dt * 1.6);
    const u = this.cyber.uniforms;
    u.uTime.value = elapsed;
    u.uSpeed.value = this.speed;
    u.uWarp.value = this._warp;
    u.uFlash.value = this._flash;
    u.uHurt.value = this._hurt;
  }

  render(dt) { this.composer.render(dt); }

  dispose() { this.composer.dispose(); }
}
